import React, { createContext, useContext, useState, useEffect } from 'react';
import { crmService } from '../services/crmService';

const CRMContext = createContext();

export const useCRM = () => {
  const context = useContext(CRMContext);
  if (!context) {
    throw new Error('useCRM must be used within a CRMProvider');
  }
  return context;
};

export const CRMProvider = ({ children }) => {
  const [contacts, setContacts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [deals, setDeals] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    console.log('🔄 [CRMContext] Loading all CRM data...');
    setLoading(true);
    try {
      const [contactsData, leadsData, dealsData, tasksData] = await Promise.all([
        crmService.getContacts(),
        crmService.getLeads(),
        crmService.getDeals(),
        crmService.getTasks()
      ]);
      
      console.log('📥 [CRMContext] Data loaded:', {
        contacts: contactsData.length,
        leads: leadsData.length,
        deals: dealsData.length,
        tasks: tasksData.length
      });
      
      setContacts(contactsData);
      setLeads(leadsData);
      setDeals(dealsData);
      setTasks(tasksData);
      
      console.log('✅ [CRMContext] All data loaded successfully');
    } catch (error) {
      console.error('❌ [CRMContext] Error loading CRM data:', error);
    } finally {
      setLoading(false);
    }
  };

  const addContact = async (contactData, profilePicture = null) => {
    console.log('➕ [CRMContext] Adding contact...', contactData);
    try {
      const newContact = await crmService.createContact(contactData, profilePicture);
      console.log('📥 [CRMContext] Contact created:', newContact);
      
      // Update local state with the new contact
      setContacts(prev => {
        const updated = [...prev, newContact];
        console.log('📋 [CRMContext] Updated contacts list:', updated.length, 'items');
        return updated;
      });
      
      console.log('✅ [CRMContext] Contact added successfully');
      return newContact;
    } catch (error) {
      console.error('❌ [CRMContext] Add contact error:', error);
      throw error;
    }
  };

  const updateContact = async (id, contactData, profilePicture = null) => {
    console.log('✏️ [CRMContext] Updating contact:', id, contactData);
    try {
      const updatedContact = await crmService.updateContact(id, contactData, profilePicture);
      console.log('📥 [CRMContext] Contact updated:', updatedContact);
      
      // Update local state
      setContacts(prev => {
        const updated = prev.map(c => c.id === id ? { ...contactData, id } : c);
        console.log('📋 [CRMContext] Updated contacts in state');
        return updated;
      });
      
      console.log('✅ [CRMContext] Contact updated successfully');
      return updatedContact;
    } catch (error) {
      console.error('❌ [CRMContext] Update contact error:', error);
      throw error;
    }
  };

  const deleteContact = async (id) => {
    console.log('🗑️ [CRMContext] Deleting contact:', id);
    try {
      await crmService.deleteContact(id);
      console.log('📥 [CRMContext] Contact deleted from backend');
      
      // Remove from local state
      setContacts(prev => {
        const updated = prev.filter(c => c.id !== id);
        console.log('📋 [CRMContext] Contact removed from state, remaining:', updated.length);
        return updated;
      });
      
      console.log('✅ [CRMContext] Contact deleted successfully');
    } catch (error) {
      console.error('❌ [CRMContext] Delete contact error:', error);
      throw error;
    }
  };

  const addLead = async (leadData) => {
    console.log('➕ [CRMContext] Adding lead...', leadData);
    try {
      const newLead = await crmService.createLead(leadData);
      setLeads(prev => [...prev, newLead]);
      return newLead;
    } catch (error) {
      console.error('❌ [CRMContext] Add lead error:', error);
      throw error;
    }
  };

  const updateLead = async (id, leadData) => {
    console.log('✏️ [CRMContext] Updating lead:', id, leadData);
    try {
      const updatedLead = await crmService.updateLead(id, leadData);
      setLeads(prev => prev.map(l => l.id === id ? { ...leadData, id } : l));
      return updatedLead;
    } catch (error) {
      console.error('❌ [CRMContext] Update lead error:', error);
      throw error;
    }
  };

  const addDeal = async (dealData) => {
    console.log('➕ [CRMContext] Adding deal...', dealData);
    try {
      const newDeal = await crmService.createDeal(dealData);
      setDeals(prev => [...prev, newDeal]);
      return newDeal;
    } catch (error) {
      console.error('❌ [CRMContext] Add deal error:', error);
      throw error;
    }
  };

  const updateDeal = async (id, dealData) => {
    console.log('✏️ [CRMContext] Updating deal:', id, dealData);
    try {
      const updatedDeal = await crmService.updateDeal(id, dealData);
      setDeals(prev => prev.map(d => d.id === id ? { ...dealData, id } : d));
      return updatedDeal;
    } catch (error) {
      console.error('❌ [CRMContext] Update deal error:', error);
      throw error;
    }
  };

  const addTask = async (taskData) => {
    console.log('➕ [CRMContext] Adding task...', taskData);
    try {
      const newTask = await crmService.createTask(taskData);
      setTasks(prev => [...prev, newTask]);
      return newTask;
    } catch (error) {
      console.error('❌ [CRMContext] Add task error:', error);
      throw error;
    }
  };

  const updateTask = async (id, taskData) => {
    console.log('✏️ [CRMContext] Updating task:', id, taskData);
    try {
      const updatedTask = await crmService.updateTask(id, taskData);
      setTasks(prev => prev.map(t => t.id === id ? { ...taskData, id } : t));
      return updatedTask;
    } catch (error) {
      console.error('❌ [CRMContext] Update task error:', error);
      throw error;
    }
  };

  const value = {
    contacts,
    leads,
    deals,
    tasks,
    loading,
    addContact,
    updateContact,
    deleteContact,
    addLead,
    updateLead,
    addDeal,
    updateDeal,
    addTask,
    updateTask,
    loadAllData
  };

  console.log('🔧 [CRMContext] CRMProvider initialized with state:', {
    contacts: contacts.length,
    leads: leads.length,
    deals: deals.length,
    tasks: tasks.length,
    loading
  });

  return (
    <CRMContext.Provider value={value}>
      {children}
    </CRMContext.Provider>
  );
};